import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FolderOpen,
  Target,
  Users,
  Mail,
  ListChecks,
  Receipt,
  FileText,
  Loader2,
  SearchX,
} from 'lucide-react';
import ModalPortal from './ModalPortal';
import { cn } from './ui/cn';
import {
  useGlobalSearch,
  type SearchCategory,
  type SearchResult,
} from '../hooks/useGlobalSearch';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

const categoryConfig: Record<
  SearchCategory,
  { label: string; icon: typeof FolderOpen; navigateTo: (r: SearchResult) => string }
> = {
  projects: {
    label: 'Projecten',
    icon: FolderOpen,
    navigateTo: (r) => `/project/${r.id}`,
  },
  leads: {
    label: 'Leads',
    icon: Target,
    navigateTo: (r) => `/lead/${r.id}`,
  },
  contacts: {
    label: 'Contacten',
    icon: Users,
    navigateTo: () => '/?tab=contacts',
  },
  emails: {
    label: 'E-mails',
    icon: Mail,
    navigateTo: () => '/?tab=inbox',
  },
  tasks: {
    label: 'Taken',
    icon: ListChecks,
    navigateTo: () => '/?tab=taken',
  },
  purchase_invoices: {
    label: 'Inkoopfacturen',
    icon: Receipt,
    navigateTo: () => '/?tab=inkoopfacturen',
  },
  sales_invoices: {
    label: 'Verkoopfacturen',
    icon: FileText,
    navigateTo: () => '/?tab=verkoopfacturen',
  },
};

const categoryOrder: SearchCategory[] = [
  'projects',
  'leads',
  'contacts',
  'emails',
  'tasks',
  'purchase_invoices',
  'sales_invoices',
];

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const { query, setQuery, results, loading, activeCategories } = useGlobalSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const grouped = categoryOrder
    .filter((cat) => activeCategories.includes(cat))
    .map((cat) => ({
      category: cat,
      items: results.filter((r) => r.category === cat),
    }))
    .filter((g) => g.items.length > 0);

  const flatItems = grouped.flatMap((g) => g.items);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(-1);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, setQuery]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      const config = categoryConfig[result.category];
      navigate(config.navigateTo(result));
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < flatItems.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : flatItems.length - 1
        );
        return;
      }

      if (e.key === 'Enter' && selectedIndex >= 0 && flatItems[selectedIndex]) {
        e.preventDefault();
        handleSelect(flatItems[selectedIndex]);
      }
    },
    [flatItems, selectedIndex, handleSelect, onClose]
  );

  useEffect(() => {
    if (selectedIndex < 0) return;
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  const hasQuery = query.trim().length >= 2;
  const showEmpty = hasQuery && !loading && flatItems.length === 0;

  const isMac = navigator.platform.toUpperCase().includes('MAC');

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
        onClick={onClose}
      >
        <div
          className="w-full max-w-xl bg-surface rounded-xl shadow-2xl border border-theme-border overflow-hidden animate-fade-in"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-theme-border">
            <Search className="w-5 h-5 text-foreground-secondary flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoeken in alles..."
              className="flex-1 bg-transparent py-4 text-base text-foreground placeholder:text-foreground-tertiary outline-none"
            />
            {loading && <Loader2 className="w-5 h-5 text-foreground-tertiary animate-spin flex-shrink-0" />}
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-theme-border bg-surface-secondary px-1.5 py-0.5 text-xs text-foreground-tertiary">
              {isMac ? '⌘' : 'Ctrl'}K
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[50vh] overflow-y-auto overscroll-contain">
            {!hasQuery && (
              <div className="px-4 py-8 text-center text-foreground-tertiary text-sm">
                Typ om te zoeken in {activeCategories.map((c) => categoryConfig[c].label.toLowerCase()).join(', ')}
              </div>
            )}

            {showEmpty && (
              <div className="px-4 py-8 text-center">
                <SearchX className="w-8 h-8 text-foreground-tertiary mx-auto mb-2" />
                <p className="text-sm text-foreground-tertiary">
                  Geen resultaten voor "<span className="font-medium text-foreground">{query}</span>"
                </p>
              </div>
            )}

            {grouped.map((group) => {
              const config = categoryConfig[group.category];
              const Icon = config.icon;
              return (
                <div key={group.category}>
                  <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-foreground-tertiary" />
                    <span className="text-xs font-semibold text-foreground-tertiary uppercase tracking-wider">
                      {config.label}
                    </span>
                  </div>
                  {group.items.map((item) => {
                    const flatIdx = flatItems.indexOf(item);
                    const isSelected = flatIdx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        data-index={flatIdx}
                        onClick={() => handleSelect(item)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                          isSelected
                            ? 'bg-brand/10 text-foreground'
                            : 'hover:bg-surface-secondary text-foreground'
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs text-foreground-tertiary truncate">
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                        {item.meta && (
                          <span className="text-xs text-foreground-tertiary whitespace-nowrap flex-shrink-0">
                            {item.meta}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          {flatItems.length > 0 && (
            <div className="px-4 py-2 border-t border-theme-border flex items-center gap-4 text-xs text-foreground-tertiary">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-theme-border bg-surface-secondary px-1 py-0.5">↑↓</kbd>
                navigeren
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-theme-border bg-surface-secondary px-1 py-0.5">↵</kbd>
                openen
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-theme-border bg-surface-secondary px-1 py-0.5">esc</kbd>
                sluiten
              </span>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
